import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { GrowthSearchOpsApi } from './growth-search-ops.api';
import {
  CreateSearchBacklogItemRequest,
  CreateSearchCoworkerRequest,
  CreateSearchCycleRequest,
  CreateSearchTaskRequest,
  SearchBacklogItemDto,
  SearchChecklistRunDto,
  SearchChecklistTemplateDto,
  SearchCoworkerDto,
  SearchCoworkerPlayDto,
  SearchCoworkerRunDto,
  SearchCoworkerScheduleDto,
  SearchCycleDto,
  SearchKeywordClusterDetailDto,
  SearchKeywordClusterListItemDto,
  SearchKeywordImportResultDto,
  SearchKeywordListItemDto,
  SearchKeywordUrlMapDto,
  SearchDecayItemDto,
  CreateAceBriefFromClusterResultDto,
  SearchPositionCatalogItemDto,
  SearchPropertyDetailDto,
  SearchPropertyGoalDto,
  SearchPropertyListItemDto,
  SearchRecommendationDto,
  SearchTaskDetailDto,
  SearchTaskListItemDto,
  SearchTeamSeatDto,
  SearchWorkHomeDto,
  SearchWorklogDto,
  UpdateSearchCoworkerRequest,
  UpsertSearchCoworkerScheduleRequest,
  UpsertSearchKeywordClusterRequest,
  UpsertSearchKeywordRequest,
  UpsertSearchKeywordUrlMapRequest,
  UpsertSearchPropertyGoalRequest,
  UpsertSearchPropertyRequest,
  UpsertSearchTeamSeatRequest,
  UpsertSearchUrlInventoryRequest,
  SearchApplyJobDto,
  SearchChangeLogDto,
  SearchCrawlRunDto,
  SearchIssueDto,
  SearchOnpageQaResultDto,
  SearchRedirectDto,
  SearchRedirectImportResultDto,
  SearchUrlInventoryDto
} from './growth-search-ops.models';

@Injectable({ providedIn: 'root' })
export class GrowthSearchOpsService {
  private readonly api = inject(GrowthSearchOpsApi);

  listPositions(): Observable<SearchPositionCatalogItemDto[]> {
    return this.api.listPositions();
  }

  getWorkHome(propertyId?: string | null): Observable<SearchWorkHomeDto> {
    return this.api.getWorkHome(propertyId);
  }

  listProperties(): Observable<SearchPropertyListItemDto[]> {
    return this.api.listProperties();
  }

  getProperty(id: string): Observable<SearchPropertyDetailDto> {
    return this.api.getProperty(id);
  }

  createProperty(payload: UpsertSearchPropertyRequest): Observable<SearchPropertyDetailDto> {
    return this.api.createProperty(payload);
  }

  updateProperty(id: string, payload: UpsertSearchPropertyRequest): Observable<SearchPropertyDetailDto> {
    return this.api.updateProperty(id, payload);
  }

  configureExternalAdapter(
    propertyId: string,
    payload: { endpointUrl: string; ackTimeoutMinutes?: number | null; rotateApiKey?: boolean }
  ) {
    return this.api.configureExternalAdapter(propertyId, payload);
  }

  listWebhookDeliveries(propertyId: string) {
    return this.api.listWebhookDeliveries(propertyId);
  }

  createSeat(propertyId: string, payload: UpsertSearchTeamSeatRequest): Observable<SearchTeamSeatDto> {
    return this.api.createSeat(propertyId, payload);
  }

  updateSeat(propertyId: string, seatId: string, payload: UpsertSearchTeamSeatRequest): Observable<SearchTeamSeatDto> {
    return this.api.updateSeat(propertyId, seatId, payload);
  }

  createGoal(propertyId: string, payload: UpsertSearchPropertyGoalRequest): Observable<SearchPropertyGoalDto> {
    return this.api.createGoal(propertyId, payload);
  }

  updateGoal(propertyId: string, goalId: string, payload: UpsertSearchPropertyGoalRequest): Observable<SearchPropertyGoalDto> {
    return this.api.updateGoal(propertyId, goalId, payload);
  }

  listChecklists(): Observable<SearchChecklistTemplateDto[]> {
    return this.api.listChecklists();
  }

  listCycles(propertyId: string): Observable<SearchCycleDto[]> {
    return this.api.listCycles(propertyId);
  }

  createCycle(propertyId: string, payload: CreateSearchCycleRequest): Observable<SearchCycleDto> {
    return this.api.createCycle(propertyId, payload);
  }

  closeCycle(cycleId: string): Observable<SearchCycleDto> {
    return this.api.closeCycle(cycleId);
  }

  listBacklog(
    propertyId: string,
    filters?: { position?: string | null; status?: string | null; cycleId?: string | null }
  ): Observable<SearchBacklogItemDto[]> {
    return this.api.listBacklog(propertyId, filters);
  }

  createBacklogItem(propertyId: string, payload: CreateSearchBacklogItemRequest): Observable<SearchBacklogItemDto> {
    return this.api.createBacklogItem(propertyId, payload);
  }

  listTasks(
    propertyId: string,
    filters?: { position?: string | null; status?: string | null; cycleId?: string | null }
  ): Observable<SearchTaskListItemDto[]> {
    return this.api.listTasks(propertyId, filters);
  }

  createTask(propertyId: string, payload: CreateSearchTaskRequest): Observable<SearchTaskListItemDto> {
    return this.api.createTask(propertyId, payload);
  }

  getTask(taskId: string): Observable<SearchTaskDetailDto> {
    return this.api.getTask(taskId);
  }

  transitionTask(taskId: string, toStatus: string): Observable<SearchTaskListItemDto> {
    return this.api.transitionTask(taskId, toStatus);
  }

  upsertTaskChecklist(taskId: string, templateKey: string, resultsJson: string): Observable<SearchChecklistRunDto> {
    return this.api.upsertTaskChecklist(taskId, templateKey, resultsJson);
  }

  addWorklog(taskId: string, minutes: number, note?: string | null): Observable<SearchWorklogDto> {
    return this.api.addWorklog(taskId, minutes, note);
  }

  listRecommendations(propertyId: string, status?: string | null): Observable<SearchRecommendationDto[]> {
    return this.api.listRecommendations(propertyId, status);
  }

  acceptRecommendation(id: string): Observable<void> {
    return this.api.acceptRecommendation(id);
  }

  rejectRecommendation(id: string): Observable<void> {
    return this.api.rejectRecommendation(id);
  }

  listCoworkers(propertyId: string): Observable<SearchCoworkerDto[]> {
    return this.api.listCoworkers(propertyId);
  }

  createCoworker(payload: CreateSearchCoworkerRequest): Observable<SearchCoworkerDto> {
    return this.api.createCoworker(payload);
  }

  getCoworker(id: string): Observable<SearchCoworkerDto> {
    return this.api.getCoworker(id);
  }

  updateCoworker(id: string, payload: UpdateSearchCoworkerRequest): Observable<SearchCoworkerDto> {
    return this.api.updateCoworker(id, payload);
  }

  pauseCoworker(id: string): Observable<void> {
    return this.api.pauseCoworker(id);
  }

  resumeCoworker(id: string): Observable<void> {
    return this.api.resumeCoworker(id);
  }

  upsertPlays(id: string, playKey: string, enabled: boolean): Observable<SearchCoworkerPlayDto[]> {
    return this.api.upsertPlays(id, playKey, enabled);
  }

  listSchedules(id: string): Observable<SearchCoworkerScheduleDto[]> {
    return this.api.listSchedules(id);
  }

  upsertSchedule(id: string, payload: UpsertSearchCoworkerScheduleRequest): Observable<SearchCoworkerScheduleDto> {
    return this.api.upsertSchedule(id, payload);
  }

  runNow(id: string): Observable<SearchCoworkerRunDto> {
    return this.api.runNow(id);
  }

  listRuns(id: string): Observable<SearchCoworkerRunDto[]> {
    return this.api.listRuns(id);
  }

  listKeywords(propertyId: string, clusterId?: string | null): Observable<SearchKeywordListItemDto[]> {
    return this.api.listKeywords(propertyId, clusterId);
  }

  upsertKeyword(propertyId: string, payload: UpsertSearchKeywordRequest): Observable<SearchKeywordListItemDto> {
    return this.api.upsertKeyword(propertyId, payload);
  }

  importKeywords(propertyId: string, csv: string): Observable<SearchKeywordImportResultDto> {
    return this.api.importKeywords(propertyId, csv);
  }

  listClusters(propertyId: string): Observable<SearchKeywordClusterListItemDto[]> {
    return this.api.listClusters(propertyId);
  }

  createCluster(propertyId: string, payload: UpsertSearchKeywordClusterRequest): Observable<SearchKeywordClusterListItemDto> {
    return this.api.createCluster(propertyId, payload);
  }

  getCluster(id: string): Observable<SearchKeywordClusterDetailDto> {
    return this.api.getCluster(id);
  }

  upsertMap(payload: UpsertSearchKeywordUrlMapRequest): Observable<SearchKeywordUrlMapDto> {
    return this.api.upsertMap(payload);
  }

  listDecay(propertyId: string): Observable<SearchDecayItemDto[]> {
    return this.api.listDecay(propertyId);
  }

  createAceBrief(clusterId: string): Observable<CreateAceBriefFromClusterResultDto> {
    return this.api.createAceBrief(clusterId);
  }

  listUrls(propertyId: string): Observable<SearchUrlInventoryDto[]> {
    return this.api.listUrls(propertyId);
  }

  upsertUrl(propertyId: string, payload: UpsertSearchUrlInventoryRequest): Observable<SearchUrlInventoryDto> {
    return this.api.upsertUrl(propertyId, payload);
  }

  runOnpageQa(
    urlId: string,
    payload: { resultsJson: string; title?: string | null; description?: string | null; createApplyJob: boolean }
  ): Observable<SearchOnpageQaResultDto> {
    return this.api.runOnpageQa(urlId, payload);
  }

  listCrawls(propertyId: string): Observable<SearchCrawlRunDto[]> {
    return this.api.listCrawls(propertyId);
  }

  runCrawl(propertyId: string, provider = 'sandbox'): Observable<SearchCrawlRunDto> {
    return this.api.runCrawl(propertyId, provider);
  }

  listIssues(propertyId: string): Observable<SearchIssueDto[]> {
    return this.api.listIssues(propertyId);
  }

  createIssue(payload: {
    searchPropertyId: string;
    urlInventoryId?: string | null;
    severity: string;
    category: string;
    evidenceJson?: string | null;
    suggestedTaskType?: string | null;
  }): Observable<SearchIssueDto> {
    return this.api.createIssue(payload);
  }

  createTaskFromIssue(issueId: string): Observable<SearchTaskListItemDto> {
    return this.api.createTaskFromIssue(issueId);
  }

  listRedirects(propertyId: string): Observable<SearchRedirectDto[]> {
    return this.api.listRedirects(propertyId);
  }

  upsertRedirect(payload: {
    searchPropertyId: string;
    fromPath: string;
    toUrl: string;
    redirectType?: string | null;
    reason?: string | null;
  }): Observable<SearchRedirectDto> {
    return this.api.upsertRedirect(payload);
  }

  importRedirects(propertyId: string, csv: string, dryRun: boolean): Observable<SearchRedirectImportResultDto> {
    return this.api.importRedirects(propertyId, csv, dryRun);
  }

  listChangeLogs(propertyId: string): Observable<SearchChangeLogDto[]> {
    return this.api.listChangeLogs(propertyId);
  }

  listApplyJobs(propertyId: string): Observable<SearchApplyJobDto[]> {
    return this.api.listApplyJobs(propertyId);
  }

  createApplyJob(payload: {
    searchPropertyId: string;
    urlInventoryId?: string | null;
    patchJson: string;
    idempotencyKey?: string | null;
  }): Observable<SearchApplyJobDto> {
    return this.api.createApplyJob(payload);
  }

  approveApplyJob(id: string): Observable<SearchApplyJobDto> {
    return this.api.approveApplyJob(id);
  }

  sendApplyJob(id: string): Observable<SearchApplyJobDto> {
    return this.api.sendApplyJob(id);
  }

  listProspects(propertyId: string) {
    return this.api.listProspects(propertyId);
  }

  upsertProspect(payload: Parameters<GrowthSearchOpsApi['upsertProspect']>[0]) {
    return this.api.upsertProspect(payload);
  }

  createTaskFromProspect(id: string) {
    return this.api.createTaskFromProspect(id);
  }

  listOutreach(propertyId: string) {
    return this.api.listOutreach(propertyId);
  }

  getOutreachFunnel(propertyId: string) {
    return this.api.getOutreachFunnel(propertyId);
  }

  upsertOutreach(payload: Parameters<GrowthSearchOpsApi['upsertOutreach']>[0]) {
    return this.api.upsertOutreach(payload);
  }

  listBacklinks(propertyId: string) {
    return this.api.listBacklinks(propertyId);
  }

  importBacklinks(propertyId: string, csv: string) {
    return this.api.importBacklinks(propertyId, csv);
  }

  listDisavow(propertyId: string) {
    return this.api.listDisavow(propertyId);
  }

  createDisavow(payload: Parameters<GrowthSearchOpsApi['createDisavow']>[0]) {
    return this.api.createDisavow(payload);
  }

  submitDisavow(id: string) {
    return this.api.submitDisavow(id);
  }

  getLocalProfile(propertyId: string) {
    return this.api.getLocalProfile(propertyId);
  }

  upsertLocalProfile(payload: Parameters<GrowthSearchOpsApi['upsertLocalProfile']>[0]) {
    return this.api.upsertLocalProfile(payload);
  }

  listCitations(propertyId: string) {
    return this.api.listCitations(propertyId);
  }

  upsertCitation(payload: Parameters<GrowthSearchOpsApi['upsertCitation']>[0]) {
    return this.api.upsertCitation(payload);
  }

  listReviewTasks(propertyId: string) {
    return this.api.listReviewTasks(propertyId);
  }

  upsertReviewTask(payload: Parameters<GrowthSearchOpsApi['upsertReviewTask']>[0]) {
    return this.api.upsertReviewTask(payload);
  }

  createTaskFromReview(id: string) {
    return this.api.createTaskFromReview(id);
  }

  listSemAccounts() {
    return this.api.listSemAccounts();
  }

  listSemCampaignLinks(accountId?: string | null) {
    return this.api.listSemCampaignLinks(accountId);
  }

  getSemWorkbook(propertyId: string) {
    return this.api.getSemWorkbook(propertyId);
  }

  upsertSemWorkbook(payload: Parameters<GrowthSearchOpsApi['upsertSemWorkbook']>[0]) {
    return this.api.upsertSemWorkbook(payload);
  }

  listNegatives(propertyId: string) {
    return this.api.listNegatives(propertyId);
  }

  upsertNegative(payload: Parameters<GrowthSearchOpsApi['upsertNegative']>[0]) {
    return this.api.upsertNegative(payload);
  }

  exportNegatives(propertyId: string) {
    return this.api.exportNegatives(propertyId);
  }

  importSearchTerms(propertyId: string, csv: string) {
    return this.api.importSearchTerms(propertyId, csv);
  }

  listAdsBriefs(propertyId: string) {
    return this.api.listAdsBriefs(propertyId);
  }

  upsertAdsBrief(payload: Parameters<GrowthSearchOpsApi['upsertAdsBrief']>[0]) {
    return this.api.upsertAdsBrief(payload);
  }

  createTaskFromAdsBrief(id: string) {
    return this.api.createTaskFromAdsBrief(id);
  }

  listBudgetWarnings(propertyId: string) {
    return this.api.listBudgetWarnings(propertyId);
  }

  getScorecard(propertyId: string) {
    return this.api.getScorecard(propertyId);
  }

  upsertScorecard(payload: Parameters<GrowthSearchOpsApi['upsertScorecard']>[0]) {
    return this.api.upsertScorecard(payload);
  }

  listAnomalies(propertyId: string) {
    return this.api.listAnomalies(propertyId);
  }

  createTaskFromAnomaly(id: string) {
    return this.api.createTaskFromAnomaly(id);
  }

  listClientReports(propertyId: string) {
    return this.api.listClientReports(propertyId);
  }

  upsertClientReport(payload: Parameters<GrowthSearchOpsApi['upsertClientReport']>[0]) {
    return this.api.upsertClientReport(payload);
  }

  getClientReport(id: string) {
    return this.api.getClientReport(id);
  }

  publishClientReport(id: string) {
    return this.api.publishClientReport(id);
  }

  listAlgoLog(propertyId: string) {
    return this.api.listAlgoLog(propertyId);
  }

  upsertAlgoLog(payload: Parameters<GrowthSearchOpsApi['upsertAlgoLog']>[0]) {
    return this.api.upsertAlgoLog(payload);
  }
}
